<template>
  <CalendarBottomSheet
    :name="name"
    :mode="mode"
    :max-date="maxDate"
    :min-date="minDate"
    :is-loading="isLoading"
    :prices="calendar.calendarPrices.value"
    :number-of-months="numberOfMonths"
    :selected-dates="calendar.state.value"
    :marked-dates="markedDates"
    :available-dates="availableDates"
    :is-new-calendar-version="isNewCalendarVersion"
    :is-filtered="isFiltered"
    @onOpen="onOpen"
    @onClose="emit('onClose')"
    @onDatesSelect="onDatesSelect"
    @onDisabledDateSelect="emit('onDisabledDateSelect')"
    @onCalendarIntersect="emit('onCalendarIntersect', $event)"
  >
    <template #header="{ hide }">
      <div class="space-y-4">
        <FreeHeader class="pt-4">
          <h3 class="text-base font-medium">
            {{ title }}
          </h3>
          <template #rightAction>
            <FreeCloseButton
              @click="hide"
            />
          </template>
        </FreeHeader>
        <slot
          name="header"
          v-bind="{
            isActiveMode: calendar.isActiveMode,
            resetDates: calendar.resetDates,
            clearSecondDate: calendar.clearSecondDate,
            dates: calendar.state.value,
          }"
        ></slot>
      </div>
    </template>
    <template #footer="{ hide }">
      <Transition name="slide-up">
        <FreeButton
          v-if="isSubmitShown && calendar.hasSelectedDates.value"
          type="button"
          size="sm"
          class="green-main-button flex h-14 w-full flex-col text-lg font-semibold"
          @click="
            () => {
              commitDates();
              hide();
            }
          "
        >
          <slot name="button-content" :dates="calendar.state.value">
            <span>
              {{
                t('searchFormCalendar.selectedDateText', {
                  date: selectedDatesButtonText,
                })
              }}
            </span>
            <span class="text-2xs font-semibold">
              {{ selectedDatePriceText }}
            </span>
          </slot>
        </FreeButton>
      </Transition>
    </template>
  </CalendarBottomSheet>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { dateUtils, searchFormCalendar } from 'shared';
  import dayjs from 'dayjs';
  import { prettyPrice } from '@aviata/text-fns';
  import type { LowPrices } from '@aviata/platform-client';

  import { FreeHeader, FreeButton, FreeCloseButton } from '../../../shared/ui';
  import CalendarBottomSheet from './CalendarBottomSheet.vue';

  const props = withDefaults(
    defineProps<{
      name: string;
      title: string;
      mode?: searchFormCalendar.config.CalendarMode;
      selectedDates: Array<string>;
      markedDates?: Array<string>;
      prices?: LowPrices.SearchCalendarResponse | null;
      maxDate?: dayjs.ConfigType;
      minDate?: dayjs.ConfigType;
      numberOfMonths?: number;
      availableDates?: Array<string>;
      isSubmitShown?: boolean;
      isNewCalendarVersion?: boolean;
      isLoading?: boolean;
      isFiltered?: boolean;
    }>(),
    {
      mode: () => searchFormCalendar.config.CALENDAR_MODES.SINGLE,
      markedDates: () => [],
      prices: null,
      maxDate: '',
      minDate: '',
      numberOfMonths: 12,
      availableDates: undefined,
      isSubmitShown: true,
      isNewCalendarVersion: false,
      isLoading: false,
    },
  );

  const emit = defineEmits<{
    onOpen: [];
    onClose: [];
    onDatesCommit: [dates: Array<string>];
    onDatesSelect: [dates: Array<string>];
    onDisabledDateSelect: [];
    onCalendarModeChange: [shouldKeepRangeMode: boolean];
    onCalendarIntersect: [visibleItems: number];
  }>();

  const { t } = useI18n();

  const calendar = searchFormCalendar.model.useSearchFormCalendar({
    selectedDates: props.selectedDates,
    selectedCalendarMode: computed(() => props.mode),
    prices: computed(() => props.prices),
    isNewCalendarVersion: props.isNewCalendarVersion,
  });

  const selectedDatesButtonText = computed(() => {
    if (calendar.hasSelectedDates.value) {
      const [first, second] = calendar.state.value;
      const isSameMonth = dayjs(first).isSame(second, 'M') && second;
      const formattedFirstDate = isSameMonth
        ? dateUtils.format(first, 'D')
        : dateUtils.getVerbalDate(first);
      if (!second) {
        return formattedFirstDate;
      }
      return `${formattedFirstDate} – ${dateUtils.getVerbalDate(second)}`;
    }
    return null;
  });

  const selectedDatePriceText = computed(() => {
    if (!props.prices) {
      return null;
    }

    const [firstDate, secondDate] = calendar.state.value;
    const isRT =
      secondDate ||
      props.mode === searchFormCalendar.config.CALENDAR_MODES.RANGE;
    const priceKey = isRT ? `${firstDate}:${secondDate}` : firstDate;
    const priceMap = isRT ? props.prices.RT : props.prices.OW;
    const localePath = isRT ? 'RT' : 'OW';

    const price = priceMap[priceKey]?.price;
    return price
      ? t(`searchFormCalendar.selectedDatePriceText.${localePath}`, {
          price: `${prettyPrice(price)} ₸`,
        })
      : '';
  });
  // Need to set selected dates and mode on open to setup selected values
  const onOpen = () => {
    calendar.updateDates(props.selectedDates);
    emit('onOpen');
  };

  const isArrivalDateSetAndSingleModeSelected = computed(
    () =>
      calendar.isActiveMode('single') &&
      calendar.state.value.length > 1 &&
      calendar.state.value[1].length > 0,
  );

  const onDatesSelect = (dates: Array<string>) => {
    if (props.isNewCalendarVersion) {
      const isNewDateOlder =
        new Date(calendar.state.value[0]) > new Date(dates[0]);

      const shouldKeepRangeMode =
        calendar.isActiveMode('range') && isNewDateOlder;
      calendar.updateDates(dates);

      if (isArrivalDateSetAndSingleModeSelected.value || shouldKeepRangeMode) {
        calendar.clearSecondDate();
      }
      emit('onDatesSelect', calendar.state.value);
      emit('onCalendarModeChange', shouldKeepRangeMode);
    } else {
      calendar.updateDates(dates);
      emit('onDatesSelect', dates);
    }
  };

  const commitDates = () => emit('onDatesCommit', calendar.state.value);

  defineExpose({
    clearSecondDate: calendar.clearSecondDate,
  });
</script>

<style scoped>
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: 0.3s;
  }

  .slide-up-enter-from {
    transform: translateY(100%);
  }

  .slide-up-leave-to {
    transform: translateY(-100%);
  }
</style>

<i18n src="../locales.json"></i18n>
